'use client';

import { type ReactNode } from 'react';
import { SModalContainer } from '../SModalContainer';
import { SFooter, type SFooterButton } from '../SFooter';
import { SOverlayHeader } from '../SOverlayHeader';

export interface SActionModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 닫기(X) 버튼 클릭 (sdClose) */
  onClose?: () => void;
  /** 백드롭·ESC로 안 닫히고 흔들림 (sd-modal-container persistent). **기본값 true** */
  persistent?: boolean;
  modalTitle?: string;
  /**
   * 제목 오른쪽에 붙는 보조 설명 (sd-action-modal 의 header-sub-title 슬롯).
   * 문자열이면 서브텍스트 스타일로 렌더하고, 노드를 넘기면 그대로 배치한다.
   */
  description?: ReactNode;
  /**
   * 하단 액션 버튼 (주 액션 1개).
   * 의도적으로 단수다 — 보조 버튼(취소·삭제 등)은 `footerLeft` 슬롯에 직접 배치한다.
   */
  button?: SFooterButton;
  /**
   * footer 좌측 영역 (sd-action-modal 의 bottom-sub-content 슬롯).
   * 보조 버튼이나 안내 문구를 넣는다. 버튼을 하나 더 쓰고 싶을 때 여기에 SButton 을 넣는다.
   */
  footerLeft?: ReactNode;
  width?: number | string;
  height?: number | string;
  children?: ReactNode;
}

/**
 * SActionModal — sd-action-modal 포팅. 헤더(제목·닫기) + 본문 + 하단 액션 버튼.
 *
 * 액션 버튼은 `button.onClick` 만 호출하고 모달을 닫지 않는다. 본문에서 API 를 호출하는 경우가
 * 많아 성공 여부에 따라 닫힘 시점이 달라지므로, 닫는 책임은 사용하는 쪽에 둔다.
 *
 * `button` 은 의도적으로 단수다. 주 액션은 오른쪽에 하나만 두고, 보조 버튼은 `footerLeft` 슬롯에
 * 소비자가 직접 배치한다. (buttons 배열 API 를 추가하지 말 것)
 */
export function SActionModal({
  open,
  onOpenChange,
  onClose,
  persistent,
  modalTitle = '',
  description,
  button,
  footerLeft,
  width,
  height,
  children,
}: SActionModalProps) {
  return (
    <SModalContainer
      open={open}
      onOpenChange={onOpenChange}
      onClose={onClose}
      persistent={persistent}
      ariaTitle={modalTitle}
      showClose
      width={width}
      height={height}
      // min-w/min-h는 화면이 그보다 좁거나 짧을 땐 양보해야 한다 — min-width/min-height가
      // max-width/max-height를 이기므로 min()으로 컨테이너의 화면 클램프와 같은 값까지만 내려가게 한다
      className="w-fit min-w-[min(480px,calc(100dvw-48px))] min-h-[min(320px,calc(100dvh-48px))]"
    >
      {/* min-h는 height가 걸리는 카드 쪽에 둔다(height<320일 때 클램핑). 내부 flex 컬럼은
          flex-auto로 카드를 채워 body(flex-1)가 남는 공간을 먹고 footer가 항상 바닥에 붙게 하며,
          카드가 화면 높이에 걸려 줄어들면 min-h-0 덕에 함께 줄어 body가 스크롤된다 */}
      <div className="flex min-h-0 flex-auto flex-col">
        <SOverlayHeader title={modalTitle} description={description} reserveCloseSpace />

        <div className="min-h-0 flex-1 overflow-auto px-[var(--cmp-modal-action-body-paddingX)] pb-[var(--cmp-modal-action-body-paddingBottom)]">
          {children}
        </div>

        {(button || footerLeft) && <SFooter bg="grey" left={footerLeft} button={button} />}
      </div>
    </SModalContainer>
  );
}
